/**
 * B6 · PROGRESSION TRACKS — TS mirror of the Rust engine
 * (`packages/core-rust/src/progression`).
 *
 * The SERVER is the authority: each stage's status, the certificate, and the
 * placement test are computed there. This mirror covers what the UI needs
 * without asking the server for permission:
 *   • the vocabulary (which scopes exist, what a "level" is called here);
 *   • the DERIVED prerequisite graph, to draw the path;
 *   • the `academy.progression.scope.<key>` manifests for the feature catalog.
 *
 * Keep IN SYNC with Rust: a new scope is added in both (there's a parity
 * test that compares the two lists).
 *
 * ## Why the code says `stage` and not `level`
 *
 * `packages/shared/src/gamification/xp.ts` already uses "level" for XP. Reusing the word for the
 * curriculum would guarantee permanent confusion between a motivational curve (can be turned off, not
 * authoritative) and something that certifies and charges. In the code they're stages; the LABEL the
 * student sees is decided by configuration.
 */

import type { FeatureManifest } from "../country/packs";
import type { CompletionConfig, CompletionNode } from "../completion/registry";

// ── Stage origins (== Rust `progression::scopes`) ─────────────────────────────

export const PROGRESSION_SCOPES = [
  "program_course",
  "program_chain",
  "course_unit",
  "manual",
] as const;

export type ProgressionScopeKey = (typeof PROGRESSION_SCOPES)[number];

/** The owner's default: **a level is a course within a program**. */
export const DEFAULT_SCOPE_KEY: ProgressionScopeKey = "program_course";

export interface ProgressionScopeMeta {
  key: ProgressionScopeKey;
  /** What a level IS with this origin (phrase shown to whoever configures it). */
  name: string;
  description: string;
  /** Params it requests, with their label for the editor. */
  params: { key: string; label: string; type: "text" | "list" | "boolean" }[];
  /** `true` if reconcile generates stages (manual generates nothing). */
  generates: boolean;
}

export const PROGRESSION_SCOPES_META: Record<ProgressionScopeKey, ProgressionScopeMeta> = {
  program_course: {
    key: "program_course",
    name: "Un nivel = un curso del programa",
    description:
      "Cada curso del programa, en su orden, es un nivel. Es la configuración por defecto: añadir un curso al programa añade un nivel sin tocar nada más.",
    params: [
      {
        key: "required_only",
        label: "Solo los cursos troncales son niveles",
        type: "boolean",
      },
    ],
    generates: true,
  },
  program_chain: {
    key: "program_chain",
    name: "Un nivel = un programa entero",
    description:
      "Una cadena de programas (por ejemplo A1 → A2 → B1), cada uno con sus propios cursos. La ruta recorre varios programas en vez de colgar de uno.",
    params: [{ key: "program_ids", label: "Programas en orden", type: "list" }],
    generates: true,
  },
  course_unit: {
    key: "course_unit",
    name: "Un nivel = una unidad del curso",
    description:
      "Cada unidad de un curso es un nivel. Pensado para talleres libres a tu ritmo, sin cohorte ni certificado por nivel.",
    params: [{ key: "course_id", label: "Curso", type: "text" }],
    generates: true,
  },
  manual: {
    key: "manual",
    name: "Los niveles los defines tú",
    description:
      "La academia crea cada nivel a mano y puede mezclar orígenes: uno anclado a un curso, otro a una cohorte, otro sin contenido que solo entrega el certificado final. La reconciliación no toca nada.",
    params: [],
    generates: false,
  },
};

/** Self-descriptive manifests for the origins (`academy.progression.scope.<key>`). */
export function progressionScopeManifests(): FeatureManifest[] {
  return PROGRESSION_SCOPES.map((key) => ({
    key: `academy.progression.scope.${key}`,
    name: PROGRESSION_SCOPES_META[key].name,
    description: PROGRESSION_SCOPES_META[key].description,
    scope: "org" as const,
    status: "beta" as const,
    requires: ["academy.progression"],
  }));
}

// ── Stage label ───────────────────────────────────────────────────────────────

export const STAGE_LABELS = ["level", "module", "cycle", "term", "stage", "custom"] as const;
export type StageLabel = (typeof STAGE_LABELS)[number];

/**
 * Effective config for a track (== Rust `ProgressionConfig`).
 * The `platform → organization → track` cascade is resolved by the server;
 * here we only read what arrives.
 */
export interface ProgressionConfig {
  scopeKey: ProgressionScopeKey | string;
  stageLabel: StageLabel;
  stageLabelCustom?: string | null;
  params?: Record<string, unknown>;
  defaultUnlock?: CompletionConfig | null;
  defaultOnComplete?: unknown[];
  nodeSource: string;
  milestoneEvery: number;
}

/** The same values as `ProgressionConfig::platform_default()` in Rust. */
export const PLATFORM_DEFAULT_CONFIG: ProgressionConfig = {
  scopeKey: DEFAULT_SCOPE_KEY,
  stageLabel: "level",
  params: {},
  nodeSource: "class_session",
  milestoneEvery: 8,
};

// ── Stage state ───────────────────────────────────────────────────────────────

export const STAGE_STATUSES = [
  "locked",
  "available",
  "in_progress",
  "completed",
  "certified",
] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

export interface StageState {
  stageId: string;
  stageKey: string;
  position: number;
  title: string;
  anchorKind: string;
  anchorId?: string | null;
  status: StageStatus;
  /** Key of the blocking criterion — the reason, not a mute lock. */
  blockedBy?: string | null;
  progressPct: number;
  unlockBreakdown?: {
    key: string;
    met: boolean;
    current: number;
    target: number;
    detail: string;
  }[];
}

export interface PrerequisiteEdge {
  fromStageKey: string;
  toStageKey: string;
  /** `false` = there's an alternative path (that's how a level skip is drawn). */
  hard: boolean;
}

export interface TrackState {
  trackId: string;
  trackName: string;
  stageLabel: StageLabel;
  stageLabelCustom?: string | null;
  stages: StageState[];
  edges: PrerequisiteEdge[];
  /** `false` when prerequisites are turned off: everything available. */
  prerequisitesEnforced: boolean;
}

// ── DERIVED graph (mirror of `progression::prerequisite_edges`) ───────────────

export interface StageRef {
  stageKey: string;
  position: number;
  unlock?: CompletionConfig | null;
}

/**
 * DERIVES the prerequisite edges from the unlock trees.
 *
 * There's no edge table on purpose: it would be a second source of truth
 * against `unlock_config` and would drift out of sync on the first edit.
 * This function can't drift, and it's tested without a server.
 */
export function prerequisiteEdges(stages: StageRef[]): PrerequisiteEdge[] {
  const ordered = [...stages].toSorted((a, b) =>
    a.position === b.position ? (a.stageKey < b.stageKey ? -1 : 1) : a.position - b.position,
  );
  const edges: PrerequisiteEdge[] = [];

  const walk = (
    node: CompletionNode,
    hard: boolean,
    stage: StageRef,
    previous: StageRef | undefined,
  ): void => {
    if (node.type === "all") {
      for (const child of node.children) {
        walk(child, hard, stage, previous);
      }
      return;
    }
    if (node.type === "any") {
      // Under an `any` there's an alternative ⇒ the edge stops being hard.
      for (const child of node.children) {
        walk(child, false, stage, previous);
      }
      return;
    }
    if (node.key !== "stage_completed") {
      return;
    }
    const params = node.params ?? {};
    const from =
      params.previous === true
        ? previous?.stageKey
        : typeof params.stage_key === "string"
          ? params.stage_key
          : undefined;
    // A stage is not a prerequisite of itself: a cycle would hang the path.
    if (from === undefined || from === stage.stageKey) {
      return;
    }
    edges.push({ fromStageKey: from, toStageKey: stage.stageKey, hard });
  };

  ordered.forEach((stage, index) => {
    if (!stage.unlock) {
      return;
    }
    walk(stage.unlock.root, true, stage, index > 0 ? ordered[index - 1] : undefined);
  });
  return edges;
}

// ── Visual path: nodes and milestones ────────────────────────────────────────

export interface PathNode {
  nodeKey: string;
  position: number;
  title: string;
  /** Milestone (end of month): distinct shape and its own celebration. */
  milestone: boolean;
}

/**
 * Numbers a stage's nodes and marks the milestone every `milestoneEvery`
 * (8 by default = 4 weeks × 2 classes = **end of month**). Exact mirror of
 * `progression::default_nodes`. `0` turns off milestones.
 */
export function pathNodes(
  items: { id: string; title: string }[],
  milestoneEvery: number,
): PathNode[] {
  return items.map((item, index) => ({
    nodeKey: item.id,
    position: index,
    title: item.title,
    // The milestone CLOSES the block (the 8th of every 8), it doesn't open it.
    milestone: milestoneEvery > 0 && (index + 1) % milestoneEvery === 0,
  }));
}
